// TypeScript types for Sistema de Agendamento UP v4.0

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  perfil: PerfilUsuario;
  perfil_id?: string; // ID do perfil no sistema de permissões granulares
  filiais: string[];
  ativo: boolean;
  created_at: string;
  updated_at: string;
  primeiro_acesso?: boolean;
  foto_perfil?: string;
}

export type PerfilUsuario = 'admin' | 'gerente' | 'operador' | 'cliente' | 'fornecedor' | 'visitante' | 'compras' | 'comprador' | 'recepcao';

export interface Filial {
  id: string;
  codigo: string;
  nome: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  ativa: boolean;
  created_at: string;
  updated_at: string;
}

export interface Agendamento {
  id: string;
  usuario_id: string;
  filial_id: string;
  data: string;
  hora?: string;
  status: StatusAgendamento;
  tipo_produto: TipoProduto;
  observacoes?: string;
  cliente_nome: string;
  cliente_telefone?: string;
  cliente_documento?: string;
  placa_veiculo?: string;
  motorista_nome?: string;
  motorista_cpf?: string;
  created_at: string;
  updated_at: string;
  
  // Joined fields
  usuario?: Usuario;
  filial?: Filial;
}

export type StatusAgendamento = 'pendente' | 'aprovado' | 'cancelado' | 'concluido';

export type TipoProduto = 'geral' | 'frios' | 'seco' | 'quimico';

export interface Carga {
  id: string;
  agendamento_id: string;
  usuario_id?: string;
  filial_id: string;
  status: StatusCarga;
  peso_kg?: number;
  volume_m3?: number;
  produtos?: string[];
  observacoes?: string;
  data_inicio?: string;
  data_conclusao?: string;
  ocorrencia?: string;
  arquivo_ocorrencia?: string;
  created_at: string;
  updated_at: string;

  // Joined fields
  agendamento?: Agendamento;
  usuario?: Usuario;
  filial?: Filial;
}

export type StatusCarga = 'aguardando' | 'em_andamento' | 'concluida' | 'cancelada';

export interface DiaFechado {
  id: string;
  data: string;
  filial_id: string;
  tipo_produto: TipoProduto;
  motivo?: string;
  created_at: string;
  
  // Joined fields
  filial?: Filial;
}

export interface Notificacao {
  id: string;
  usuario_id: string;
  titulo: string;
  mensagem: string;
  lida: boolean;
  tipo: 'info' | 'success' | 'warning' | 'error';
  agendamento_id?: string;
  created_at: string;
  
  // Joined fields
  agendamento?: Agendamento;
}

export interface Auditoria {
  id: string;
  usuario_id?: string;
  acao: string;
  tabela: string;
  registro_id?: string;
  valores_antigos?: any;
  valores_novos?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  
  // Joined fields
  usuario?: Usuario;
}

// Form types
export interface LoginForm {
  email: string;
  senha: string;
}

export interface RegisterForm {
  nome: string;
  email: string;
  senha: string;
  confirmarSenha: string;
}

export interface AgendamentoForm {
  filial_id: string;
  data: string;
  hora?: string;
  tipo_produto: TipoProduto;
  observacoes?: string;
  cliente_nome: string;
  cliente_telefone?: string;
  cliente_documento?: string;
  placa_veiculo?: string;
  motorista_nome?: string;
  motorista_cpf?: string;
}

export interface UsuarioForm {
  nome: string;
  email: string;
  perfil: PerfilUsuario;
  filiais: string[];
  ativo: boolean;
  foto_perfil?: string;
}

export interface FilialForm {
  codigo: string;
  nome: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  ativa: boolean;
}

// Dashboard types
export interface DashboardStats {
  totalAgendamentos: number;
  agendamentosHoje: number;
  agendamentosPendentes: number;
  cargasEmAndamento: number;
  usuariosAtivos: number;
  filiaisAtivas: number;
}

export interface AgendamentosPorStatus {
  pendente: number;
  aprovado: number;
  cancelado: number;
  concluido: number;
}

export interface AgendamentosPorFilial {
  filial: string;
  total: number;
  pendentes: number;
  aprovados: number;
}

// API Response types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Filter types
export interface AgendamentoFilters {
  status?: StatusAgendamento[];
  tipo_produto?: TipoProduto[];
  filial_id?: string[];
  data_inicio?: string;
  data_fim?: string;
  usuario_id?: string;
}

export interface CargaFilters {
  status?: StatusCarga[];
  filial_id?: string[];
  data_inicio?: string;
  data_fim?: string;
  usuario_id?: string;
}

export interface UsuarioFilters {
  perfil?: PerfilUsuario[];
  ativo?: boolean;
  filial?: string;
}

// Context types
export interface AuthContextType {
  user: Usuario | null;
  loading: boolean;
  login: (email: string, password: string, captcha?: string) => Promise<Usuario | null>;
  logout: () => Promise<void>;
  register: (data: RegisterForm) => Promise<void>;
  updateProfile: (data: Partial<UsuarioForm>) => Promise<void>;
  temPermissao: (permissao: string) => boolean;
}

export interface AppContextType {
  filialAtual: string;
  setFilialAtual: (filial: string) => void;
  setFilialAtualComValidacao: (filial: string) => void;
  setFilialAtualSemValidacao: (filial: string) => void;
  notificacoesNaoLidas: number;
  refreshNotificacoes: () => Promise<void>;
}

// Component props types
export interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

export interface InputProps {
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  type?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  onRowClick?: (item: T) => void;
  className?: string;
}

export interface TableColumn<T> {
  key: keyof T;
  title: string;
  render?: (value: any, item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

// Utility types
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

// ========== SISTEMA DE SOLICITAÇÕES DE AGENDAMENTO ==========

export enum StatusSolicitacao {
  PENDENTE = 'Pendente',
  AGUARDANDO_PEDIDO = 'Aguardando Pedido',
  PEDIDO_DIGITADO = 'Pedido digitado',
  APROVADO = 'APROVADO',
  REPROVADO = 'REPROVADO',
  REAGENDADO = 'REAGENDADO'
}

export interface Documento {
  id: string;
  nome: string;
  tipo: string;
  tamanho: number;
  fornecedorMercadoria: string;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
  url?: string;
  uploadSucesso?: boolean;
}

export interface ArquivoPedido {
  id: string;
  databaseKey: string;
  nome: string;
  tipo: string;
  tamanho: number;
  numeroPedido?: string;
  storagePath: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface PedidoDigitado {
  id: string;
  numeroPedido: string;
  arquivo: ArquivoPedido;
  documentoOriginal?: Documento;
  digitadoPor: string;
  dataDigitacao: string;
  uploadedAt?: string;
  uploadedBy?: string;
}

export interface Reagendamento {
  novaData: string;
  motivo: string;
  propostoPor: string;
  dataProposta: string;
}

export interface Comentario {
  id: string;
  texto: string;
  usuarioId: string;
  usuarioNome: string;
  createdAt: string;
  updatedAt: string;
}

export interface SolicitacaoAgendamento {
  id: string;
  solicitante_id: string;
  solicitante_nome: string;
  fornecedor: string;
  fornecedor_mercadoria?: string;
  mercadoria_descricao?: string; // Descrição da mercadoria informada pelo fornecedor
  data_desejada: string;
  peso: number;
  volume: number | string;
  tipo_produto: string;
  tipo_carga: string;
  conferente: string;
  status: StatusSolicitacao;
  documentos?: Documento[];
  arquivo_pedido?: ArquivoPedido;
  documentos_digitados?: PedidoDigitado[];
  reagendamento_proposto?: Reagendamento;
  pedido_compra_id?: string;
  mercadoria_a_enviar: string; // Descrição da mercadoria a enviar (campo obrigatório)
  comentarios?: Comentario[];
  observacoes?: string;
  data_solicitacao: string;
  data_atualizacao: string;
  filial: string;
  prioridade?: number;
  motivo_reprovacao?: string;
  ordem_chegada?: number; // Ordem de chegada da carga
  hora_ordem_chegada?: string; // Horário de chegada da carga
}

// ========== SISTEMA DE AUDIT TRAIL ==========

export enum TipoAcaoAudit {
  CRIAR = 'criar',
  ATUALIZAR = 'atualizar',
  DELETAR = 'deletar',
  APROVAR = 'aprovar',
  REPROVAR = 'reprovar',
  REAGENDAR = 'reagendar',
  ANEXAR_DOCUMENTO = 'anexar_documento',
  CRIAR_PEDIDO = 'criar_pedido',
  COMENTAR = 'comentar',
  LOGIN = 'login',
  LOGOUT = 'logout'
}

export interface AuditLog {
  id: string;
  usuarioId: string;
  usuarioNome: string;
  acao: TipoAcaoAudit;
  tabela: string;
  registroId?: string;
  valoresAntigos?: any;
  valoresNovos?: any;
  motivo?: string;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface AppContextType {
  filialAtual: string;
  setFilialAtual: (filial: string) => void;
  setFilialAtualComValidacao: (filial: string) => void;
  setFilialAtualSemValidacao: (filial: string) => void;
  notificacoesNaoLidas: number;
  refreshNotificacoes: () => Promise<void>;
}

// ========== SISTEMA DE WORKFLOW POR FILIAL ==========

export interface WorkflowFilial {
  id: string;
  filialId: string;
  regras: WorkflowRegras;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowRegras {
  niveisAprovacao: number;
  aprovadoresObrigatorios: string[];
  tempoMaximoAprovacao: number; // em horas
  notificarAprovadores: boolean;
  notificarSolicitante: boolean;
  permitirReagendamento: boolean;
  permitirMultiplasSolicitacoes: boolean;
}

// ========== SISTEMA DE NOTIFICAÇÕES EM TEMPO REAL ==========

export enum TipoNotificacao {
  SOLICITACAO_CRIADA = 'solicitacao_criada',
  SOLICITACAO_APROVADA = 'solicitacao_aprovada',
  SOLICITACAO_REPROVADA = 'solicitacao_reprovada',
  SOLICITACAO_REAGENDADA = 'solicitacao_reagendada',
  PEDIDO_CRIADO = 'pedido_criado',
  PEDIDO_DIGITADO = 'pedido_digitado',
  COMENTARIO_ADICIONADO = 'comentario_adicionado',
  AGENDAMENTO_REALIZADO = 'agendamento_realizado',
  CARGA_STATUS_MUDADO = 'carga_status_mudado',
  CARGA_OCORRENCIA = 'carga_ocorrencia',
  CARGA_ORDEM_CHEGADA = 'carga_ordem_chegada',
  CARGA_PRIORIDADE_MUDADA = 'carga_prioridade_mudada',
  SOLICITACAO_ACESSO_CRIADA = 'solicitacao_acesso_criada',
  SOLICITACAO_ACESSO_APROVADA = 'solicitacao_acesso_aprovada',
  SOLICITACAO_ACESSO_REPROVADA = 'solicitacao_acesso_reprovada'
}

export interface NotificacaoRealtime {
  id: string;
  usuarioId: string;
  tipo: TipoNotificacao;
  titulo: string;
  mensagem: string;
  dados?: any;
  lida: boolean;
  createdAt: string;
}

// ========== SISTEMA DE DASHBOARD ANALÍTICO ==========

export interface DashboardAnalitico {
  metricasGerais: MetricasGerais;
  metricasAprovacao: MetricasAprovacao;
  metricasPorFilial: MetricasPorFilial[];
  metricasPorPeriodo: MetricasPorPeriodo[];
}

export interface MetricasGerais {
  totalSolicitacoes: number;
  solicitacoesPendentes: number;
  solicitacoesAprovadas: number;
  solicitacoesReprovadas: number;
  pedidosCriados: number;
  tempoMedioAprovacao: number; // em horas
}

export interface MetricasAprovacao {
  taxaAprovacao: number; // porcentagem
  taxaReprovacao: number; // porcentagem
  principaisMotivosReprovacao: MotivoReprovacao[];
}

export interface MotivoReprovacao {
  motivo: string;
  quantidade: number;
  porcentagem: number;
}

export interface MetricasPorFilial {
  filial: string;
  totalSolicitacoes: number;
  aprovadas: number;
  reprovadas: number;
  tempoMedioAprovacao: number;
}

export interface MetricasPorPeriodo {
  periodo: string; // ex: "2024-01", "2024-02"
  totalSolicitacoes: number;
  aprovadas: number;
  reprovadas: number;
}

// ========== SISTEMA DE AGENDAMENTO DE VISITAS ==========

export type StatusVisita =
  | 'PENDENTE'
  | 'APROVADO'
  | 'REPROVADO'
  | 'REAGENDADO'
  | 'CANCELADO'
  | 'CHEGOU'
  | 'EM_ATENDIMENTO'
  | 'FINALIZADO'
  | 'NAO_COMPARECEU';

export interface Visita {
  id: string;
  codigo: string;
  solicitante_id: string;
  comprador_id: string;
  data_visita: string;
  horario_inicio: string;
  horario_fim: string;
  assunto: string;
  documento: string;
  empresa_representada: string;
  nome_visitante?: string;
  email_visitante?: string;
  telefone_visitante?: string;
  foto_visitante_url?: string;
  status: StatusVisita;
  motivo_reprovacao?: string;
  motivo_cancelamento?: string;
  observacoes?: string;
  codigo_verificacao?: string;
  data_aprovacao?: string;
  data_chegada?: string;
  data_inicio_atendimento?: string;
  data_fim_atendimento?: string;
  tempo_espera_minutos?: number;
  tempo_atendimento_minutos?: number;
  criado_em: string;
  atualizado_em: string;

  // Transferência
  motivo_transferencia_id?: string;
  motivo_transferencia_texto?: string;
  observacao_transferencia?: string;

  // Cancelamento com motivo
  motivo_cancelamento_id?: string;
  motivo_cancelamento_texto?: string;
  observacao_cancelamento?: string;

  // Finalização com motivo
  motivo_finalizacao_id?: string;
  motivo_finalizacao_texto?: string;
  observacao_finalizacao?: string;

  // Não compareceu
  nao_compareceu?: boolean;
  data_nao_compareceu?: string;

  // Joined fields
  solicitante?: Usuario;
  comprador?: Usuario;
  checkin?: VisitaCheckin[];
}

export interface VisitaAgendaComprador {
  id: string;
  comprador_id: string;
  dia_semana: number;
  horario_inicio: string;
  horario_fim: string;
  horario_inicio_almoco?: string;
  horario_fim_almoco?: string;
  intervalo_minutos: number;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
}

export interface VisitaFechamento {
  id: string;
  comprador_id: string;
  data: string;
  horario_inicio?: string;
  horario_fim?: string;
  motivo?: string;
  tipo: 'manual' | 'feriado';
  ativo: boolean;
  criado_por?: string;
  criado_em: string;
  atualizado_em: string;
}

export interface VisitaCheckin {
  id: string;
  visita_id: string;
  recepcionista_id?: string;
  nome_recepcionista?: string;
  documento_confirmado?: string;
  empresa_confirmada?: string;
  observacoes?: string;
  data_hora: string;
  criado_em: string;
}

export interface VisitaHistoricoStatus {
  id: string;
  visita_id: string;
  status_anterior?: StatusVisita;
  status_novo: StatusVisita;
  observacoes?: string;
  alterado_por?: string;
  alterado_em: string;

  // Joined fields
  alterado_por_usuario?: Usuario;
}

export interface VisitaSlot {
  inicio: string;
  fim: string;
  ocupado?: boolean;
  status?: StatusVisita;
}

export interface VisitaComprovante {
  id: string;
  codigo: string;
  codigo_verificacao?: string;
  solicitante: string;
  empresa: string;
  documento: string;
  comprador: string;
  data: string;
  horario: string;
  assunto: string;
  status: StatusVisita;
  gerado_em: string;
}

export interface VisitaIndicadores {
  por_status: Record<string, number>;
  por_status_percentual: Record<string, number>;
  total_agendamentos: number;
  agendamentos_realizados: number;
  agendamentos_aprovados: number;
  taxa_aprovacao: number;
  taxa_recusa: number;
  taxa_cancelamento: number;
  taxa_reagendamento: number;
  tempo_medio_aprovacao_minutos: number;
  tempo_medio_espera_minutos: number;
  tempo_medio_atendimento_minutos: number;
  tempo_medio_permanencia_minutos: number;
  tempo_medio_liberacao_recepcao_minutos: number;
  tempo_medio_solicitacao_visita_dias: number;
  pontualidade_visitantes: number;
  comparecimento: number;
  faltas_no_show: number;
  indice_atendimento_concluido: number;
  comprador_maior_volume: { nome: string; quantidade: number } | null;
  fornecedor_mais_atendido: { nome: string; quantidade: number } | null;
  representante_mais_visitas: { nome: string; quantidade: number } | null;
  top_compradores: { nome: string; quantidade: number }[];
  top_fornecedores: { nome: string; quantidade: number }[];
  top_representantes: { nome: string; quantidade: number }[];
  piores_fornecedores: { nome: string; total_visitas: number; faltas: number; atrasos: number; reprovados: number; taxa_falta: number; taxa_atraso: number; taxa_reprovacao: number; score: number }[];
  horarios_mais_utilizados: { faixa: string; quantidade: number }[];
  evolucao_diaria: { data: string; quantidade: number; dia_semana: string }[];
  taxa_ocupacao_agenda: number;
  total_finalizadas: number;
  visitas_hoje: number;
  agendamentos_sem_visita_presencial: number;
}

// ========== SISTEMA DE PEDIDOS DE COMPRA ==========

export interface Pedido {
  id: string;
  numero_pedido?: string;
  solicitacao_id?: string;
  fornecedor: string;
  data_desejada: string;
  peso: number;
  volume: number | string;
  tipo_produto: string;
  tipo_carga: string;
  observacoes?: string;
  status: 'Aguardando pedido' | 'Pedido digitado' | 'Aprovado' | 'Reprovado';
  criado_em: string;
  atualizado_em: string;
  criado_por: string;
  prioridade?: number;
  documentos?: PedidoDigitado[];
  motivo_reprovacao?: string;
}
